/* global Stripe */

import { getAnalytics } from '~entrypoints/analytics';
import isEmailAddress from '~Utils/is-email-address';

const CARD_OPTIONS = {
  hidePostalCode: true,
  classes: {
    base: 'mb-0 text-grey-darker text-base sm:pt-2 p-2 sm:h-10',
    focus: 'outline-hidden',
    invalid: 'text-danger',
  },
  style: {
    base: {
      color: '#333',
      lineHeight: '1.5rem',
      fontFamily: "'Source Sans Pro', sans-serif",
      fontSize: '16px',
      '::placeholder': {
        color: '#777',
      },
    },
  },
};

export default function ({
  publishableKey = undefined,
  defaultCardHolderEmail,
  defaultSubmitButtonText,
  leadSource,
  program,
  requireCardHolderEmail = true,
  requireCountry = false,
}) {
  return {
    cardHolderEmail: defaultCardHolderEmail || '',
    cardHolderFullName: '',
    userEmail: '',
    isGift: false,
    country: '',
    postalCode: '',
    address1: '',
    address2: '',
    state: '',
    city: '',
    token: '',
    isCardFieldFocused: false,
    isCardValid: false,
    isEnabled: false,
    isErrorMessagesDisplayed: false,
    isReady: false,
    cardErrors: 'Card number is invalid',
    sourceSystem: '',

    init() {
      this.leadSource = leadSource;
      this.program = program;
      this.submitButtonLabel = defaultSubmitButtonText;
      this.stripe = Stripe(publishableKey);
    },

    install() {
      const elements = this.stripe.elements();
      const card = elements.create('card', CARD_OPTIONS);

      card.mount(this.$refs['card-mount']);

      card.on('ready', () => {
        this.isEnabled = true;
        this.isReady = true;
      });

      card.on('focus', () => {
        this.isCaRdFieldFocused = true;
      });

      card.on('blur', () => {
        this.isCardFieldFocused = false;
      });

      card.on('change', ({ error }) => {
        if (error) {
          this.isCardValid = false;
          this.cardErrors = error.message;
        } else {
          this.isCardValid = true;
          this.cardErrors = '';
        }
      });

      this.card = card;
    },

    get isValid() {
      return (
        this.isCardHolderEmailValid &&
        this.isCardHolderFullNameValid &&
        this.isGiftValid &&
        this.isCardValid &&
        this.isCountryValid &&
        this.isPostalCodeValid &&
        this.isAddress1Valid &&
        this.isStateValid &&
        this.isCityValid
      );
    },

    validateForm() {
      this.isErrorMessagesDisplayed = true;
    },

    async onSubmit() {
      getAnalytics().track('Checkout Button Pressed');

      if (!this.isValid) {
        this.handleInvalidForm();
        return;
      }

      await this.identifyUser();

      await this.tokenizeCard();
    },

    async tokenizeCard() {
      const { card, cardHolderFullName, cardHolderEmail } = this;

      // Create a PaymentMethod (pm_...) rather than a legacy token. The backend
      // confirms a SetupIntent against it, which is what enables 3DS/SCA.
      const result = await this.stripe.createPaymentMethod({
        type: 'card',
        card,
        billing_details: {
          name: cardHolderFullName,
          email: cardHolderEmail,
          address: this.billingAddress,
        },
      });

      if (result.paymentMethod) {
        this.handleTokenizationSuccess(result.paymentMethod.id);
      } else {
        this.handleTokenizationFailure(result.error);
      }
    },

    async identifyUser() {
      const {
        $store: { sessionId: distinctId },
        cardHolderEmail: email,
        program,
        leadSource,
      } = this;

      return await getAnalytics().identify(distinctId, { email, program, leadSource });
    },

    handleInvalidForm() {
      this.isEnabled = true;
      this.isErrorMessagesDisplayed = true;
      this.submitButtonLabel = 'Try again';
    },

    async handleTokenizationSuccess(token) {
      this.token = token;
      this.sourceSystem = 'Stripe';

      // NOTE: You need to wait for the update above to render before submitting
      await this.$nextTick();

      this.$refs['payment-form'].submit();
    },

    /**
     * @see {@link https://stripe.com/docs/api/errors}
     */
    handleTokenizationFailure(error) {
      getAnalytics().track('Checkout Validation Failure');

      this.handleInvalidForm();

      switch (error.type) {
        case 'card_error':
        case 'validation_error':
          this.isErrorMessagesDisplayed = true;
          this.cardErrors = error.message;
          break;

        case 'api_error':
        case 'idempotency_error':
        case 'invalid_request_error':
          this.$dispatch(
            'flash-message',
            'An error has occurred. Please try again or contact support.'
          );

          break;

        default:
          this.$dispatch(
            'flash-message',
            'An error has occurred. Please try again or contact support.'
          );

          break;
      }
    },

    get isCardInvalid() {
      return this.isErrorMessagesDisplayed && !this.isCardValid;
    },

    get isCardHolderEmailValid() {
      return !requireCardHolderEmail || isEmailAddress(this.cardHolderEmail);
    },

    get isCardHolderEmailInvalid() {
      return this.isErrorMessagesDisplayed && !this.isCardHolderEmailValid;
    },

    get isCardHolderFullNameValid() {
      return this.cardHolderFullName.length > 1;
    },

    get isCardHolderFullNameInvalid() {
      return this.isErrorMessagesDisplayed && !this.isCardHolderFullNameValid;
    },

    get isGiftValid() {
      if (!this.isGift) {
        return true;
      }

      return isEmailAddress(this.userEmail);
    },

    get isGiftInvalid() {
      return this.isErrorMessagesDisplayed && !this.isGiftValid;
    },

    // Billing address is collected after a first failed attempt (requireCountry)
    // to give Stripe AVS data on retry. Mirrors the Spreedly form: country is
    // always required once collection is on; the rest are India-only.
    get billingAddress() {
      return {
        line1: this.address1 || null,
        line2: this.address2 || null,
        city: this.city || null,
        state: this.state || null,
        postal_code: this.postalCode || null,
        country: this.country || null,
      };
    },

    requireState() {
      return ['IN' /*'CA', 'US'*/].includes(this.country);
    },

    requirePostalCode() {
      return ['IN'].includes(this.country);
    },

    requireCity() {
      return ['IN'].includes(this.country);
    },

    requireAddress1() {
      return ['IN'].includes(this.country);
    },

    get isCountryValid() {
      return !requireCountry || this.country.length === 2;
    },

    get isCountryInvalid() {
      return this.isErrorMessagesDisplayed && !this.isCountryValid;
    },

    get isPostalCodeValid() {
      return !this.requirePostalCode() || this.postalCode.length > 1;
    },

    get isPostalCodeInvalid() {
      return this.isErrorMessagesDisplayed && !this.isPostalCodeValid;
    },

    get isAddress1Valid() {
      return !this.requireAddress1() || this.address1.length > 1;
    },

    get isAddress1Invalid() {
      return this.isErrorMessagesDisplayed && !this.isAddress1Valid;
    },

    get isAddress2Valid() {
      return true;
    },

    get isAddress2Invalid() {
      return !this.isAddress2Valid;
    },

    get isStateValid() {
      return !this.requireState() || this.state.length > 1;
    },

    get isStateInvalid() {
      return this.isErrorMessagesDisplayed && !this.isStateValid;
    },

    get isCityValid() {
      return !this.requireCity() || this.city.length > 1;
    },

    get isCityInvalid() {
      return this.isErrorMessagesDisplayed && !this.isCityValid;
    },
  };
}
